import { useLiveQuery } from 'dexie-react-hooks'
import { listStampers } from '@/core/repository/stamperRepository'
import { labelColorOn } from '@/design/contrast'
import type { StamperRow } from '@/db/types'
import { useStatCatalog } from '@/features/analysis/useStatCatalog'
import './stampers.css'

interface StamperStripProps {
  onApply: (stamper: StamperRow) => void
  disabled: boolean
}

/**
 * The one-tap row above the categories.
 *
 * A stamper collapses three decisions — type, object, comment — into one tap.
 * It is the difference between logging "Gym w/ Ben" in one gesture and doing it
 * in four, and four is enough friction to stop bothering.
 *
 * Renders nothing at all when there are no stampers: an empty strip with a
 * "create one" prompt would take permanent space in the one part of the app
 * that has none to spare.
 */
export function StamperStrip({ onApply, disabled }: StamperStripProps) {
  const stampers = useLiveQuery(() => listStampers(), [], [])
  const catalog = useStatCatalog()

  if (stampers.length === 0) return null

  return (
    <div className="st-strip" role="group" aria-label="Stampers">
      {stampers.map((stamper) => {
        const colour = stamper.eventTypeId
          ? (catalog.types.get(stamper.eventTypeId)?.colorHex ?? '#8A97AA')
          : '#8A97AA'

        const objectName = stamper.eventObjectId
          ? (catalog.objects.get(stamper.eventObjectId)?.name ?? null)
          : null

        // Spelled out for assistive tech, because the visible label is the
        // stamper's nickname and says nothing about what it will actually do.
        const description = [
          stamper.eventTypeId ? (catalog.types.get(stamper.eventTypeId)?.name ?? null) : null,
          objectName,
          stamper.comment,
        ]
          .filter(Boolean)
          .join(', ')

        return (
          <button
            key={stamper.id}
            className="st-chip"
            style={{ background: colour, color: labelColorOn(colour) }}
            onClick={() => onApply(stamper)}
            disabled={disabled}
            aria-label={`${stamper.name}${description ? `: ${description}` : ''}`}
            title={description || stamper.name}
          >
            <span className="st-chip-mark" aria-hidden="true">
              ✦
            </span>
            {stamper.name}
          </button>
        )
      })}
    </div>
  )
}
